// JMEFIT Stripe Products - Updated January 2025
// Centralized product and price ID management

export const STRIPE_PRODUCTS = {
  NUTRITION_ONLY: {
    id: 'prod_Tmiag8fL6Q2dT6',
    name: 'Nutrition Only Program - Updated',
    monthly_price_id: 'price_1RUDhrG00IiCtQkDvaOpyt6s', // $199.00/month — UPDATE THIS IN STRIPE DASHBOARD
    yearly_price_id: 'price_1RUDijG00IiCtQkDe1qmQ0eK',  // $1910.40/year — UPDATE THIS IN STRIPE DASHBOARD
    monthly_amount: 19900,
    yearly_amount: 191040,
    category: 'nutrition',
    commitment_period: '3_months'
  },
  
  NUTRITION_TRAINING: {
    id: 'prod_SP1lr30wNw70q6',
    name: 'Nutrition & Training - Complete Package', 
    monthly_price_id: 'price_1RUDi3G00IiCtQkD4uRbb21q', // $249.00/month
    yearly_price_id: 'price_1RUDioG00IiCtQkDpJ8b1kWn',  // $2390.40/year
    monthly_amount: 24900,
    yearly_amount: 239040,
    category: 'nutrition_training',
    commitment_period: '3_months'
  },
  
  SELF_LED_TRAINING: {
    id: 'prod_SP1ll102SXAam9',
    name: 'Self-Led Training Program',
    monthly_price_id: 'price_1RUDiCG00IiCtQkDoCAAviT3', // $24.99/month
    yearly_price_id: 'price_1RUDitG00IiCtQkDnlnwaDnP',  // $239.90/year  
    monthly_amount: 2499,
    yearly_amount: 23990,
    category: 'training',
    commitment_period: 'none'
  },
  
  TRAINER_FEEDBACK: {
    id: 'prod_SP1lX3tDKbN2Xy',
    name: 'Trainer Feedback Program',
    monthly_price_id: 'price_1RUDiLG00IiCtQkDXmMxfyQV', // $49.99/month
    yearly_price_id: 'price_1RUDixG00IiCtQkDrQevwF4a',  // $431.90/year
    monthly_amount: 4999,
    yearly_amount: 43190,
    category: 'training_premium',
    commitment_period: 'none'
  },
  
  ONE_TIME_MACROS: {
    id: 'prod_SP1lF2nvedXRIh',
    name: 'One-Time Macros Calculation',
    price_id: 'price_1RUDiUG00IiCtQkD2xc2mQSs', // $99.00 one-time
    amount: 9900,
    category: 'one_time',
    commitment_period: 'none'
  },
  
  SHRED_CHALLENGE: {
    id: 'prod_SP1mq6XLLy27oL', 
    name: 'SHRED with JMEFit Challenge',
    price_id: 'price_1RUDieG00IiCtQkDPXpSZB8x', // $297.00 one-time
    amount: 29700,
    category: 'challenge',
    commitment_period: 'none'
  }
} as const;

// Helper functions for price calculations
export const getPriceAmount = (productKey: keyof typeof STRIPE_PRODUCTS, interval?: 'month' | 'year') => {
  const product = STRIPE_PRODUCTS[productKey];
  
  if (!interval) {
    return 'amount' in product ? product.amount : product.monthly_amount;
  }
  
  if (interval === 'month') {
    return 'monthly_amount' in product ? product.monthly_amount : product.amount;
  }
  
  if (interval === 'year') {
    return 'yearly_amount' in product ? product.yearly_amount : product.amount;
  }
  
  return 0;
};

export const getPriceId = (productKey: keyof typeof STRIPE_PRODUCTS, interval?: 'month' | 'year') => {
  const product = STRIPE_PRODUCTS[productKey];
  
  if (!interval) {
    return 'price_id' in product ? product.price_id : product.monthly_price_id;
  }
  
  if (interval === 'month') {
    return 'monthly_price_id' in product ? product.monthly_price_id : product.price_id;
  }
  
  if (interval === 'year') {
    return 'yearly_price_id' in product ? product.yearly_price_id : product.price_id;
  }
  
  return '';
};

// Format price for display (convert from cents to dollars)
export const formatPrice = (amountInCents: number) => {
  return (amountInCents / 100).toFixed(2);
};

// =============================================================================
// Display-ready pricing (in USD dollars) — SINGLE SOURCE OF TRUTH
// All components must import prices from here. NEVER hardcode prices in components.
// =============================================================================
export const PRICING = {
  NUTRITION_ONLY: {
    name: 'Nutrition Only',
    monthly: 199,
    yearly: 1910.40,  // $199 x 12 x 0.8 (20% annual discount)
    stripe_product_id: STRIPE_PRODUCTS.NUTRITION_ONLY.id,
    monthlyPriceId: STRIPE_PRODUCTS.NUTRITION_ONLY.monthly_price_id,
    yearlyPriceId: STRIPE_PRODUCTS.NUTRITION_ONLY.yearly_price_id,
    commitment: '3 months',
    category: 'nutrition',
  },
  NUTRITION_TRAINING: {
    name: 'Nutrition & Training',
    monthly: 249,
    yearly: 2390.40,  // matches Stripe yearly_amount / 100
    stripe_product_id: STRIPE_PRODUCTS.NUTRITION_TRAINING.id,
    monthlyPriceId: STRIPE_PRODUCTS.NUTRITION_TRAINING.monthly_price_id,
    yearlyPriceId: STRIPE_PRODUCTS.NUTRITION_TRAINING.yearly_price_id,
    commitment: '3 months',
    category: 'nutrition_training',
  },
  SELF_LED: {
    name: 'Self-Led Training',
    monthly: 24.99,
    yearly: 239.90,  // matches Stripe yearly_amount / 100
    stripe_product_id: STRIPE_PRODUCTS.SELF_LED_TRAINING.id,
    monthlyPriceId: STRIPE_PRODUCTS.SELF_LED_TRAINING.monthly_price_id,
    yearlyPriceId: STRIPE_PRODUCTS.SELF_LED_TRAINING.yearly_price_id,
    commitment: null,
    category: 'training',
  },
  TRAINER_FEEDBACK: {
    name: 'Trainer Feedback',
    monthly: 49.99,
    yearly: 431.90,  // matches Stripe yearly_amount / 100 (actual Stripe price)
    stripe_product_id: STRIPE_PRODUCTS.TRAINER_FEEDBACK.id,
    monthlyPriceId: STRIPE_PRODUCTS.TRAINER_FEEDBACK.monthly_price_id,
    yearlyPriceId: STRIPE_PRODUCTS.TRAINER_FEEDBACK.yearly_price_id,
    commitment: null,
    category: 'training_premium',
  },
  ONE_TIME_MACROS: {
    name: 'One-Time Macros Calculation',
    price: 99,
    stripe_product_id: STRIPE_PRODUCTS.ONE_TIME_MACROS.id,
    priceId: STRIPE_PRODUCTS.ONE_TIME_MACROS.price_id,
    category: 'one_time',
  },
  SHRED_CHALLENGE: {
    name: 'SHRED Challenge',
    price: 297,
    stripe_product_id: STRIPE_PRODUCTS.SHRED_CHALLENGE.id,
    priceId: STRIPE_PRODUCTS.SHRED_CHALLENGE.price_id,
    category: 'challenge',
  },
} as const;

// Helper: get price for display based on interval
export const getDisplayPrice = (key: keyof typeof PRICING, interval?: 'month' | 'year'): number => {
  const product = PRICING[key];
  if ('price' in product) return product.price; // one-time
  if (interval === 'year') return product.yearly;
  return product.monthly;
};

// Helper: get stripe price ID based on interval
export const getStripePriceId = (key: keyof typeof PRICING, interval?: 'month' | 'year'): string => {
  const product = PRICING[key];
  if ('priceId' in product) return product.priceId;
  if (interval === 'year') return product.yearlyPriceId;
  return product.monthlyPriceId;
};

// Helper: get the monthly price for any product key (used for reverse calculations)
export const getMonthlyPrice = (key: keyof typeof PRICING): number => {
  const product = PRICING[key];
  if ('price' in product) return product.price; // one-time products
  return product.monthly;
};